import moment from "moment";
import cloneDeep from "lodash/cloneDeep";

// Only certain columns are relevant for comparison.
export const supportedDifferences = [{
    field: "RegularHours",
    isCurrency: false,
    caption: "Regular Hours"
}, {
    field: "RegularAmount",
    isCurrency: true,
    caption: "Regular Hours Amount"
}, {
    field: "OvertimeHours",
    isCurrency: false,
    caption: "Overtime Hours"
}, {
    field: "OvertimeAmount",
    isCurrency: true,
    caption: "Overtime Amount"
}, {
    field: "DoubleTimeHours",
    isCurrency: false,
    caption: "Double Time Hours",
    visible: p => p.ShowDT
}, {
    field: "DoubleTimeAmount",
    isCurrency: true,
    caption: "Double Time Amount",
    visible: p => p.ShowDT
}, {
    field: "ExtraHoursWorked",
    isCurrency: false,
    caption: "Extra Hours Worked"
}, {
    field: "TotalHoursWorked",
    isCurrency: false,
    caption: "Total Hours Worked"
}, {
    field: "TotalGuaranteed",
    isCurrency: true,
    caption: "Total Guaranteed",
}, {
    field: "RegularCommission",
    isCurrency: true,
    caption: "Regular Commission",
    canBeIndirectlyAffected: true
}, {
    field: "TotalCommissionAmount",
    isCurrency: true,
    caption: "Total Paid Commission",
    canBeIndirectlyAffected: true
}, {
    field: "MealPeriodPremiumAmount",
    isCurrency: true,
    caption: "Meal Period Premium Amount"
}, {
    field: "Salary",
    isCurrency: true,
    caption: "Salary",
    visible: p => p.ShowSalary
}, {
    field: "ExtraCommission",
    isCurrency: true,
    caption: "Extra Commission"
}, {
    field: "CommissionBonus",
    isCurrency: true,
    caption: "Commission Bonus"
}, {
    field: "SickPayAmount",
    isCurrency: false,
    caption: "Paid Sick Leave",
    visible: p => p.ShowSick
}, {
    field: "ReportingTimeAmount",
    isCurrency: false,
    caption: "Reporting Time",
    visible: p => p.ShowReportingTime
}, {
    field: "SplitTimeAmount",
    isCurrency: true,
    caption: "Split Shift Premium",
    visible: p => p.ShowSplit
}, {
    field: "MealPeriodPremium",
    isCurrency: true,
    caption: "Meal Period Premium",
    visible: p => p.ShowMealPeriodPremium
}, {
    field: "HolidayPay",
    isCurrency: true,
    caption: "Holiday Pay",
    visible: p => p.ShowHolidayPay
}, {
    field: "TravelAllowance",
    isCurrency: true,
    caption: "Travel Allowance",
    visible: p => p.ShowTravel
}, {
    field: "CellAllowance",
    isCurrency: true,
    caption: "Cell Allowance",
    visible: p => p.ShowCell
}, {
    field: "Tip",
    isCurrency: true,
    caption: "Tip",
    visible: p => p.ShowTip
}, {
    field: "MinimumWageAdvance",
    isCurrency: true,
    caption: "Minimum Wage Advance",
    canBeIndirectlyAffected: true,
    visible: p => p.ShowMinimumWageAdvance
}, {
    field: "Chargeback",
    isCurrency: true,
    caption: "Chargeback",
    canBeIndirectlyAffected: true,
    visible: p => p.ShowChargeback
}, {
    field: "TrainingPay",
    isCurrency: true,
    caption: "Training Pay",
    visible: p => p.ShowTrainingPay
}, {
    field: "GrossPay",
    isCurrency: true,
    caption: "Gross Pay",
    canBeIndirectlyAffected: true
}, {
    field: "NetPayAddition",
    isCurrency: true,
    caption: "Net Pay Addition"
}, {
    field: "NetPayDeduction",
    isCurrency: true,
    caption: "Net Pay Deduction"
}]

// Earning definitions that are compared between the whole pay stub.
const differablePayStubEarnings = [{
    rate: p => p.RegularRate,
    units: p => p.RegularHours,
    amount: p => p.RegularAmount,
    combineWeeks: true, // Even if there are differences in multiple weeks, combine both into one line.
    unit: "hour",
    caption: "Regular"
}, {
    rate: p => p.RegularRate,
    units: p => p.TrainingPayHours,
    amount: p => p.TrainingPay,
    unit: "hour",
    combineWeeks: true,
    caption: "Training Pay"
}, {
    rate: p => p.OvertimeRate,
    units: p => p.OvertimeHours,
    amount: p => p.OvertimeAmount,
    unit: "hour",
    caption: "Overtime"
}, {
    rate: p => p.DoubleTimeRate,
    units: p => p.DoubleTimeHours,
    amount: p => p.DoubleTimeAmount,
    unit: "hour",
    caption: "Double Time"
}, {
    amount: p => p.TotalCommissionAmount || 0,
    caption: "Commissions"
}, {
    amount: p => p.Chargeback || 0,
    caption: "Chargeback"
}, {
    amount: p => p.Bonus,
    caption: "Discretionary Bonus"
}, {
    amount: p => p.CommissionBonus,
    caption: "Bonus"
}, {
    amount: p => p.Salary,
    caption: "Salary"
}, {
    amount: p => p.CellAllowance,
    caption: "Cell Allowance",
    combineWeeks: true
}, {
    amount: p => p.Tip,
    caption: "Tip",
    combineWeeks: true
}, {
    amount: p => p.TravelAllowance,
    caption: "Travel Allowance",
    combineWeeks: true
}, {
    rate: p => p.RegularRate,
    units: p => p.MealPeriodPremiumTime,
    amount: p => p.MealPeriodPremiumAmount,
    unit: "hour",
    combineWeeks: true,
    caption: "Meal Period Premium"
}, {
    rate: p => p.RegularRate,
    units: p => p.SickPay,
    amount: p => p.SickPayAmount,
    combineWeeks: true,
    unit: "hour",
    caption: "Paid Sick Leave"
}, {
    rate: p => p.RegularRate,
    units: p => p.SplitTime,
    amount: p => p.SplitTimeAmount,
    unit: "hour",
    combineWeeks: true,
    caption: "Split Shift Premium"
}, {
    amount: p => p.HolidayPay,
    caption: "Statutory Holiday Pay"
}, {
    rate: p => p.RegularRate,
    units: p => p.ReportingTime,
    amount: p => p.ReportingTimeAmount,
    unit: "hour",
    combineWeeks: true,
    caption: "Reporting Time"
}];

/**
 * A service implementing calculations for processing manual submissions.
 */
export class PayrollManualCalculationService {
    constructor() {
        'ngInject';
        this.supportedDifferences = supportedDifferences;
    }

    /**
     * Return an altered RS Commission Amounts array that adds to or updates the RS
     * Commission list to include the extra show hours.
     * @param {any} input The base payroll input
     * @param {any[]} extraShowHours The extra show hours array.
     */
    addExtraHourRSCommissionAmounts(input, extraShowHours) {

        const rsCommissions = cloneDeep(input.RSCommissionAmounts);
        return extraShowHours.reduce((rsCommissions, showHour) => {
            // eslint-disable-next-line eqeqeq
            const commission = rsCommissions.find(p => p.ProgramID == showHour.ProgramID
                // eslint-disable-next-line eqeqeq
                && p.StoreID == showHour.EventHostID
                && moment(p.ScheduleDate).isSame(showHour.Date, "day"));
            const totalHours = moment.duration(showHour.TotalDuration).asHours();
            if (commission) {
                commission.ManHoursWorked += totalHours;
            } else {
                rsCommissions.push({
                    ProgramID: showHour.ProgramID,
                    StoreID: showHour.EventHostID,
                    ScheduleDate: showHour.Date,
                    CommissionAmount: 0,
                    ManHoursWorked: totalHours,
                    SalesAmount: 0,
                    SalesNetPayout: 0,
                    TierRate: 0,
                    DailyNCRThreshold: 0
                });
            }

            return rsCommissions;
        }, rsCommissions);
    }

    /**
     * Return an altered pay date durations array consisting of the original
     * input combined with the extra show hours from a manual request.
     * @param {any} input The base payroll input.
     * @param {any[]} extraShowHours The extra show hours array.
     */
    addExtraHourPayDateDurations(input, payrollEntry, extraShowHours) {
        // Send extra hours in as input. The commission amount will override any
        // proportional
        return input.PayDateDurations.concat(
            extraShowHours.map(extraHour => ({
                Date: extraHour.Date,
                ProgramID: extraHour.ProgramID,
                StoreID: extraHour.EventHostID,
                DivisionID: payrollEntry.Divisions[0],
                TotalHours: moment.duration(extraHour.TotalDuration).asHours(),
                EmployeeID: payrollEntry.EmployeeID,
                PayRate: payrollEntry.EmployeePayRate,
                CommissionAmount: extraHour.CommissionAmount
            })))
    }

    /**
     * Generate a list of differences between two sets of payroll calculations.
     * @param {any[]} left The list of current payroll entry calculations
     * @param {any[]} right The list of updated payroll entry calculations
     */
    enumerateDifferences(left, right) {
        return left.reduce((employeeGroups, payPeriod) => {
            const updated = right.find(p => p.PayPeriodID === payPeriod.PayPeriodID);
            if (!updated) {
                return employeeGroups;
            }
            let employeeGroup = employeeGroups.find(p => p.employee.EmployeeID === payPeriod.Employee.EmployeeID);
            if (!employeeGroup) {
                employeeGroup = {
                    employee: payPeriod.Employee,
                    payPeriodChanges: [],
                    hasDifference: false
                }

                employeeGroups.push(employeeGroup);
            }

            const payPeriodChange = {
                payPeriodID: payPeriod.PayPeriodID,
                employee: payPeriod.Employee,
                weekNumber: payPeriod.WeekNumber,
                differences: []
            };

            for (const column of supportedDifferences.filter(p => !p.visible || p.visible(payPeriod))) {
                const difference = getNumberDifference(column.field, payPeriod, updated) || {
                    ...column
                };
                difference.caption = column.caption;
                difference.isCurrency = column.isCurrency;
                payPeriodChange.differences.push(difference);
                if (difference.difference) {
                    employeeGroup.hasDifference = true;
                }
            }

            employeeGroup.payPeriodChanges.push(payPeriodChange);

            return employeeGroups;

        }, [])
            // Group by employee so that both weeks can be returned.
            .filter(p => p.hasDifference)
            .reduce((payPeriodChanges, employeeGroup) =>
                payPeriodChanges.concat(employeeGroup.payPeriodChanges), []);

    }

    comparePayStubs(left, right) {
        const fullPayStub = [];
        const differences = [];
        const periodEarnings = differablePayStubEarnings
            .concat([left, right].reduce((pieceEarnings, payPeriods) => {
                // Define earnings for each commission piece.
                // Note: due to how commission pieces are not archived, there will never be a
                // difference in commission piece count directly. However, this can still be used
                // when generating the earnings for a full pay stub.
                for (const period of payPeriods) {
                    for (const piece of period.CommissionPieces) {
                        if ((piece.PieceQty || piece.PieceRate) && !pieceEarnings.dict[piece.PieceNumber]) {
                            pieceEarnings.dict[piece.PieceNumber] = true;
                            // Note that the piece in scope right now is for a specific pay period. To use the
                            // pay period as passed in, access the commission piece by its index.
                            pieceEarnings.functions.push({
                                rate: p => p.CommissionPieces[piece.PieceNumber - 1].PieceRate || null,
                                units: p => p.CommissionPieces[piece.PieceNumber - 1].PieceQty || null,
                                amount: p => {
                                    const _piece = p.CommissionPieces[piece.PieceNumber - 1];
                                    return _piece.PieceRate * _piece.PieceQty || 0;
                                },
                                combineWeeks: true,
                                caption: `Commission Piece #${piece.PieceNumber}`,
                                unit: "qty",
                            });
                        }
                    }
                }
                return pieceEarnings;
            }, { functions: [], dict: {} }).functions);

        for (const earnings of periodEarnings) {
            const leftAmount = this.enumerateEarnings(earnings, left);
            const rightAmount = this.enumerateEarnings(earnings, right);
            const earningsDifferences = this.subtractEarnings(rightAmount, leftAmount); // Subtract original from updated, so reverse the argument order.
            differences.push(...earningsDifferences);
            fullPayStub.push(...rightAmount);
        }
        // If there is no difference, assume that if the manual was requested,
        // the manual pay stub is being produced for the first time,
        // and therefore all amounts on the pay stub should appear on the manual submission's pay stub.
        const res = differences.length ? differences : fullPayStub;
        return res.flat().filter(p => p.amount || p.units);
    }

    /**
     * Produce a list of earnings lines for a particular type of earnings given the pay periods
     * comprising the pay stub.
     * @param {any} earnings A specific earning being enumerated.
     * @param {any} payPeriods The list of pay periods in the enumerated pay stub.
     * @param {boolean} isMultiWeek Indicates if the pay stub is multi-week.
     */
    enumerateEarnings(earnings, payPeriods) {
        return payPeriods.reduce((earningsLines, period) => {
            let earningsLine = earningsLines[0];
            if (!earningsLine) {
                earningsLine = {
                    caption: earnings.caption,
                    rate: earnings.rate ? earnings.rate(period) : null,
                    units: earnings.units ? 0 : null, // Either null or 0 base depending on if units are used.
                    amount: 0
                };
                earningsLines.push(earningsLine);
            }
            if (earnings.units) {
                earningsLine.units += earnings.units(period);
            }
            earningsLine.amount += earnings.amount(period);
            return earningsLines;
        }, []).flat();
    }

    /**
     * Subtract earnings in the right operand from earnings in the left operand, and return
     * the result.
     * @param {any[]} left The left operand, the amount to be added.
     * @param {any[]} right The right operand, the amount to be subtracted.
     */
    subtractEarnings(left, right) {
        const differences = [];
        for (const earningsLine of left) {
            const rightLine = right.find(p => p.caption === earningsLine.caption);
            const leftAmount = earningsLine.amount || 0;
            const rightAmount = rightLine ? rightLine.amount || 0 : 0;
            if (!rightLine || leftAmount !== rightAmount) {
                differences.push({
                    ...earningsLine,
                    units: earningsLine.units !== null
                        ? earningsLine.units - (rightLine ? rightLine.units || 0 : 0) : null,
                    amount: leftAmount - rightAmount
                });
            }
        }
        return differences;
    }

    getBaselineTotal(payPeriod) {
        const baseline = {
            PayPeriodID: payPeriod.PayPeriodID,
            WeekNumber: payPeriod.WeekNumber,
            Employee: payPeriod.Employee

        };

        return supportedDifferences.reduce((baseline, difference) => {
            // Ensure the field value is a number. Otherwise, the comparison
            // will be rejected outright.
            baseline[difference.field] = 0;
            return baseline;

        }, baseline)

    }

    /**
     * Return an initial list of pay stub earnings for a particular manual submission.
     * @param {any} manualSubmission The manual submission being initialized.
     * @param {any} left The current payroll entry calculations.
     * @param {any} right The updated payroll entry calculations.
     * @returns The initialized list of earnings
     */
    initializePayStubEarnings(manualSubmission, left, right) {
        console.log(manualSubmission);
        const payPeriod = right.find(p => p.PayPeriodID === manualSubmission.PayPeriodID);
        left = left.filter(p => p.EmployeeID === payPeriod.EmployeeID &&
            p.PayPeriodID === manualSubmission.PayPeriodID);
        right = right.filter(p => p.EmployeeID === payPeriod.EmployeeID &&
            p.PayPeriodID === manualSubmission.PayPeriodID);
        const differences = this.comparePayStubs(left, right);
        console.log(differences);
        const updatedGrossPay = right.reduce((sum, p) => sum + p.GrossPay, 0);
        const currentGrossPay = left.reduce((sum, p) => sum + p.GrossPay, 0);
        const updatedNetPayAddition = right.reduce((sum, p) => sum + (p.NetPayAddition || 0), 0);
        const currentNetPayAddition = left.reduce((sum, p) => sum + (p.NetPayAddition || 0), 0);
        const differenceGrossPay = updatedGrossPay - currentGrossPay || updatedGrossPay;
        const differenceNetPayAddition = updatedNetPayAddition - currentNetPayAddition || updatedNetPayAddition;
        return {
            Earnings: differences.map(difference => ({
                Caption: difference.caption,
                Rate: difference.rate !== null && difference.caption !== "Meal Period Premium" ?
                    Math.round(difference.rate * 100) / 100 : null,
                Units: difference.units !== null && difference.caption !== "Meal Period Premium" ?
                    Math.round(difference.units * 100) / 100 : null,
                Amount: difference.amount !== null ? Math.round(difference.amount * 100) / 100 : null
            })),
            GrossPay: Math.round(differenceGrossPay * 100) / 100,
            NetPayAddition: Math.round(differenceNetPayAddition * 100) / 100
        };
    }

    /**
     * Map from difference to a pay stub earning.
     * @param {{ differences: { caption: string, difference: number, isCurrency: boolean}[] }} differences
     * @returns {{ Name: string, Value: number, IsCurrency: boolean}[]}
     */
    createPayStubEarnings(differences) {
        return differences.differences.filter(difference => difference.difference)
            .map(difference => ({
                Name: difference.caption,
                Value: difference.difference,
                IsCurrency: difference.isCurrency
            }));
    }
}


function getNumberDifference(field, left, right) {
    const rightValue = right[field];
    const leftValue = left[field]
    //If anyone of the left or right is a valid number
    if ((!isNaN(leftValue) || !isNaN(rightValue)) && Number(leftValue) !== Number(rightValue)) {
        return {
            field,
            left: leftValue,
            right: rightValue,
            difference: rightValue - leftValue
        }
    }
}

